import { useCallback, useEffect, useMemo, useState } from 'react'

import { FlattenedDocumentPage } from '@/lib/api/client'
import { useDocumentState } from '@/state'
import { ColumnDef } from '@tanstack/react-table'
import {
  TemplateAttribute,
  Table,
  useDeleteTable,
  useUpdateTable,
  TableRow,
} from '@/services/hooks/template_attributes'
import { EditableTable, createNewColumn } from './EditableTable'

type TableData = {
  [accessor: string]: any
}
type GenericTableProps = {
  template: TemplateAttribute<Table>
  isReadOnly?: boolean
  documentPage: FlattenedDocumentPage
  dataType: 'generic-table'
  onNudgeStart?: (showGrid: boolean) => void
  onNudgeEnd?: (hideGrid: boolean) => void
}
export const GenericTable = ({
  template,
  isReadOnly = false,
  documentPage,
  dataType,
  onNudgeStart,
  onNudgeEnd,
}: GenericTableProps) => {
  const setSelectedElement = useDocumentState(
    (state) => state.setSelectedElement,
  )
  const tableId = useMemo(() => template?.id || '', [template])

  const [selectedRow, setSelectedRow] = useState<number | null>(null)
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null)

  const selectTable = useCallback(() => {
    setSelectedElement({
      documentPageId: documentPage.id as string,
      id: tableId,
      isRemovable: false,
      type: dataType,
    })
  }, [documentPage.id, tableId, setSelectedElement])

  const initialTableData = useMemo(
    () => template?.template_values.rows,
    [template?.template_values.rows],
  )
  const initialColumns = useMemo(
    () => template?.template_values.columns,
    [template?.template_values.columns],
  )
  const tablePosition = useMemo(
    () => template?.template_values.position,
    [template?.template_values.position],
  )
  const columnsSize = useMemo(
    () => template?.template_values.columnsSize,
    [template?.template_values.columnsSize],
  )

  const [tableData, setTableData] = useState<Array<TableData>>([])
  const [columnsDef, setColumnsDef] = useState<Array<ColumnDef<TableData>>>([])
  const [columnsData, setColumnsData] = useState<TableData>({})

  const { mutateAsync: updateTable } = useUpdateTable()
  const { mutateAsync: deleteTable, isPending: isDeletingTable } =
    useDeleteTable()

  const isDeleting = useMemo(() => isDeletingTable, [isDeletingTable])

  const deleteTableHandler = useCallback(async () => {
    await deleteTable({
      templateAttributeId: tableId,
      documentPageId: documentPage.id as string,
    })
  }, [deleteTable, tableId])

  useEffect(() => {
    if (initialColumns) {
      setColumnsData((prev) => ({
        ...prev,
        ...initialColumns.reduce(
          (accum, column) => ({
            ...accum,
            ...column,
          }),
          {},
        ),
      }))

      initialColumns.forEach((column) => {
        Object.keys(column).forEach((accessorKey) => {
          const columnDef = createNewColumn({
            accessorKey,
            onCellChange,
            selectTable,
            isReadOnly,
            documentPageId: documentPage.id as string,
            tableType: 'generic-table',
          })
          setColumnsDef((prev) => {
            return [...prev, columnDef]
          })
        })
      })
    }

    return () => {
      setColumnsDef([])
    }
  }, [initialColumns])

  useEffect(() => {
    if (initialTableData) {
      setTableData((prev) => [
        ...prev.map((row, i) => ({
          ...row,
          ...(initialTableData[i] || {}),
        })),
        ...initialTableData,
      ])
    }

    return () => {
      setTableData([])
    }
  }, [initialTableData])

  const onCellChange = useCallback(
    (rowIndex, columnId, value) => {
      setTableData((prev) =>
        prev.map((row, index) =>
          index === rowIndex ? { ...row, [columnId]: value } : row,
        ),
      )
    },
    [setTableData],
  )

  const onHeaderChange = useCallback((value, accessorKey: string) => {
    setColumnsData((prev) => ({
      ...prev,
      [accessorKey]: value,
    }))
  }, [])

  const saveTableData = useCallback(
    async ({
      columns,
      rows,
    }: {
      columns?: Array<{ [key: string]: any }>
      rows?: Array<TableRow>
    }) => {
      const tableData: Table = {
        type: dataType,
      }

      if (columns) {
        tableData.columns = columns
      }

      if (rows) {
        tableData.rows = rows
      }

      await updateTable({
        templateAttributeId: template?.id as string,
        documentPageId: documentPage.id as string,
        tableData,
      })
    },
    [
      documentPage.id,
      tableId,
      tableData,
      columnsData,
      updateTable,
      dataType,
      columnsDef,
    ],
  )

  const saveGenericTableData = useCallback(async () => {
    const cleanColumns = columnsDef.reduce((accum, colDef: any) => {
      const columnAccessor = Object.keys(columnsData).find(
        (accessorKey) => accessorKey === colDef.accessorKey,
      )

      if (!columnAccessor) return accum

      const column = columnsData[columnAccessor]

      return [
        ...accum,
        {
          [columnAccessor]: column,
        },
      ]
    }, [] as Array<TableData>)
    await saveTableData({
      columns: cleanColumns,
      rows: tableData,
    })
  }, [tableData, columnsData, columnsDef, saveTableData])

  const addNewColumn = useCallback(
    async ({
      accessorKey,
      newColumn,
      position,
    }: {
      accessorKey: string
      newColumn: ColumnDef<any>
      position: 'left' | 'right'
    }) => {
      const newColumnAccessor = (newColumn as { accessorKey: string })
        .accessorKey
      const targetIndex = columnsDef.findIndex(
        (column) => (column as any).accessorKey === accessorKey,
      )
      const insertIndex = position === 'left' ? targetIndex : targetIndex + 1
      const updatedColumns = [
        ...columnsDef.slice(0, insertIndex),
        { accessorKey: newColumnAccessor },
        ...columnsDef.slice(insertIndex),
      ].map((columnDef: any) => {
        const key = columnDef.accessorKey
        return { [key]: columnsData[key] ?? '' }
      })
      const updatedRows = [
        ...tableData.map((row) => ({
          ...row,
          [newColumnAccessor]: '',
        })),
      ]

      await saveTableData({
        columns: updatedColumns,
        rows: updatedRows,
      })
    },
    [columnsDef, tableData, saveTableData],
  )

  const removeColumn = useCallback(
    async (accessorKey: string) => {
      if (columnsDef.length === 1) {
        await deleteTableHandler()
        return
      }

      const columnBefore = columnsDef.findIndex(
        (column) => (column as any).accessorKey === accessorKey,
      )

      if (columnBefore === -1) return
      setColumnsDef((prevColumns) => [
        ...prevColumns.slice(0, columnBefore),
        ...prevColumns.slice(columnBefore + 1),
      ])
      setTableData((prevTableData) =>
        prevTableData.filter((row) => Object.keys(row).includes(accessorKey)),
      )
      setColumnsData((prev) => {
        const newColumns = { ...prev }
        delete newColumns[accessorKey]
        return newColumns
      })

      const filteredRows = tableData
        .map((row) => {
          return Object.keys(row).reduce((acc, currAccessorKey) => {
            if ((currAccessorKey as string) !== accessorKey) {
              acc[currAccessorKey] = row[currAccessorKey]
            }

            return acc
          }, {})
        })
        .filter((row) => Object.keys(row).length)

      const filteredColumns = columnsDef
        .filter(
          (column) =>
            ((column as any).accessorKey as string).includes('custom-column') &&
            ((column as any).accessorKey as string) !== accessorKey,
        )
        .map((column) => {
          const accessorKey = (column as any).accessorKey
          return { [accessorKey]: columnsData[accessorKey] }
        })

      await saveTableData({
        columns: filteredColumns,
        rows: filteredRows,
      })
    },
    [columnsDef, columnsData, tableData, setColumnsDef, saveTableData],
  )

  const removeRow = useCallback(
    async (rowIndex) => {
      const filteredRows = tableData.filter((_, index) => index !== rowIndex)
      setTableData((prev) => prev.filter((_, index) => index !== rowIndex))

      await saveTableData({
        rows: filteredRows,
      })
    },
    [tableData, setTableData, saveTableData],
  )

  const addNewRow = useCallback(
    async ({
      rowIndex,
      newRow,
      position,
    }: {
      rowIndex?: number
      newRow: any
      position: 'above' | 'below'
    }) => {
      let insertIndex: number

      if (typeof rowIndex === 'number') {
        insertIndex = position === 'above' ? rowIndex : rowIndex + 1
      } else {
        insertIndex = position === 'above' ? 0 : tableData.length
      }

      const updatedRows = [
        ...tableData.slice(0, insertIndex),
        newRow,
        ...tableData.slice(insertIndex),
      ]
      setTableData(updatedRows)

      await saveTableData({
        rows: updatedRows,
      })
    },
    [tableData],
  )

  useEffect(() => {
    if (columnsSize) {
      setColumnsDef((prevColumns) =>
        prevColumns.map((column) => {
          const accessor = (column as any).accessorKey
          const size = (columnsSize as any)[accessor]
          if (size) {
            return {
              ...column,
              size,
            }
          }
          return column
        }),
      )
    }
  }, [columnsSize])

  return (
    <EditableTable
      saveTableData={saveGenericTableData}
      onCellChange={onCellChange}
      removeRow={removeRow}
      addRow={addNewRow}
      onHeaderChange={onHeaderChange}
      saveCustomColumns={saveGenericTableData}
      removeColumn={removeColumn}
      addColumn={addNewColumn}
      setSelectedColumn={setSelectedColumn}
      setSelectedRow={setSelectedRow}
      selectedRow={selectedRow}
      selectedColumn={selectedColumn}
      documentPage={documentPage}
      tableData={tableData}
      defaultRows={0}
      columnsDef={columnsDef as any}
      columnsData={columnsData}
      positionData={tablePosition}
      columnsSize={columnsSize || {}}
      tableTemplate={template as any}
      isDeleting={isDeleting}
      isReadOnly={isReadOnly}
      id={tableId}
      type={dataType}
      onNudgeEnd={onNudgeEnd}
      onNudgeStart={onNudgeStart}
    />
  )
}

export default GenericTable
